<template>
  <div class="content-text">
    <h2 class="title-2 text-left">10 Conclusão</h2>

    <p>
      O Quadro 13 apresenta o desempenho atual do município em relação a
      quantidade de equipes distribuídas por classificação. Como o valor do
      Componente Equidade é fixo, estando atualmente em
      <strong> {{ formatCurrency(dados?.etapa4?.totalEquidade || 0) }}</strong
      >, ele vai diminuir somente se ocorrer suspensão de recursos pelas regras
      de suspensão
    </p>

    <p>
      A maior oportunidade de maximizar o recebimento dos recursos federais para
      a APS está nos componentes de Qualidade e CVAT. No quadro 13, o “Total vs
      Ótimo” mostra o valor a mais que o município pode receber quando todas as
      equipes municipais estiverem na classificação ÓTIMA.
    </p>

    <div class="container-table resumo-equipes-tabela">
      <span style="text-transform: uppercase; font-weight: bold"
        >Quadro 13 - CENÁRIO DAS EQUIPES POR COMPONENTE E CLASSIFICAÇÃO
      </span>
      <table>
        <thead>
          <tr>
            <th>COMPONENTE</th>
            <th>QUANTIDADE</th>
            <th>CLASSIFICAÇÃO</th>
            <th>VALOR</th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="(item, index) in resumoTabela" :key="index">
            <td>{{ item.componente }}</td>
            <td>{{ item.quantidade }} {{ item.equipe }}</td>
            <td>{{ item.classificacaoLabel }}</td>
            <td class="text-right">
              <strong>{{ formatCurrency(item.valorTotal) }}</strong>
            </td>
          </tr>
        </tbody>

        <tfoot>
          <tr class="grey">
            <td colspan="3"><strong>Total</strong></td>
            <td class="text-right">
              <strong>{{ formatCurrency(totalAtual) }}</strong>
            </td>
          </tr>

          <tr class="grey">
            <td colspan="3"><strong>Total vs Ótimo</strong></td>
            <td class="text-right text-danger">
              <strong>{{ formatCurrency(totalVsOtimo) }}</strong>
            </td>
          </tr>
        </tfoot>
      </table>
      <span class="fonte"
        ><strong>Fonte:</strong> Autoria própria, maio/2026.</span
      >
    </div>

    <p>
      Entende-se que o valor de
      <b>{{ formatCurrency(totalVsOtimo) }}</b>
      no repasse mensal ou
      <b>{{ formatCurrency(totalVsOtimoAnual) }}</b>
      considerando o repasse anual (diferença entre o desempenho
      <b>"ÓTIMO"</b> e <b>"ATUAL"</b>, apresentado no quadro 13) é
      representativo para o município melhorar os serviços da APS, focando em
      mais qualidade.
    </p>

    <p>
      Para o desempenho <b>"ÓTIMO"</b>, faz-se necessário o trabalho conjunto e
      colaborativo entre a equipe gestora de saúde, profissionais de
      informática, monitoramento e avaliação e os profissionais de saúde atuando
      orientados pelas Boas Práticas, de forma integrada no cuidado contínuo aos
      cidadãos do território.
    </p>
  </div>
</template>

<script>
import { regras, getLabelRegra } from '../utils.js'

export default {
  props: {
    dados: {
      type: Object,
      default: () => {},
    },
    nomeMunicipio: {
      type: String,
      default: '',
    },
    diferencaMensalOtimoRegular: {
      type: Number,
      default: 0,
    },
    diferencaAnualOtimoRegular: {
      type: Number,
      default: 0,
    },
    fonte: {
      type: String,
      default: '',
    },
  },
  data() {
    return {}
  },
  mounted() {},
  computed: {
    equipesEtapa4() {
      return this.dados?.etapa4?.equipes || []
    },

    resumoTabela() {
      return this.equipesEtapa4.map((item) => {
        return {
          ...item,
          componente: item.componente === 'CVAT' ? 'CVAT' : 'Qualidade',
        }
      })
    },

    /**
     * Retorna as equipes consideradas no fixo de custeio/equidade.
     *
     * @returns {Array}
     */
    equipesEquidade() {
      const totais = this.dados?.etapa4?.totaisEquipes || {}

      return [
        {
          key: 'eSF 40h',
          quantidade: Number(totais.eSF || 0),
        },
        {
          key: 'eAP 30h',
          quantidade: Number(totais['eAP 30h'] || 0),
        },
        {
          key: 'eAP 20h',
          quantidade: Number(totais['eAP 20h'] || 0),
        },
      ].filter((item) => item.quantidade > 0)
    },

    /**
     * Retorna a descrição das equipes da equidade.
     *
     * @returns {String}
     */
    descricaoEquidade() {
      return this.equipesEquidade
        .map((item) => `${item.quantidade} ${item.key}`)
        .join(', ')
    },

    /**
     * Retorna o total atual da tabela.
     *
     * @returns {Number}
     */
    totalAtual() {
      const totalResumo = this.resumoTabela.reduce((total, item) => {
        return total + Number(item.valorTotal || 0)
      }, 0)

      return totalResumo
    },

    /**
     * Retorna a diferença entre o cenário ótimo e o atual.
     *
     * @returns {Number}
     */
    totalVsOtimo() {
      return Number(this.dados?.etapa4?.totalPerda || 0)
    },

    /**
     * Retorna a diferença anual entre o cenário ótimo e o atual.
     *
     * @returns {Number}
     */
    totalVsOtimoAnual() {
      return this.totalVsOtimo * 12
    },
  },
  methods: {
    /**
     * Formata um valor em moeda brasileira.
     *
     * @param {Number} value
     * @returns {String}
     */
    formatCurrency(value) {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(Number(value || 0))
    },
  },
}
</script>

<style lang="scss">
.resumo-equipes-tabela {
  break-inside: avoid;
  page-break-inside: avoid;
  margin-bottom: 10px;
}

@media print {
  .resumo-equipes-tabela {
    break-inside: avoid;
    page-break-inside: avoid;
    margin-bottom: 10px;
  }

  .resumo-equipes-tabela th {
    background-color: #156082 !important;
    color: #fff !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
}
</style>
